import React, { useEffect, useMemo } from "react";
import _ from 'lodash';
import cx from 'classnames';
import dateformat from "dateformat";

import styles from './styles.module.scss';
import { fetchStreamChildren, fetchStreams } from "src/redux/actions/streams";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import Reveal from "src/components/animation";
import Tooltip from "src/components/tooltip";
import { fetchCollection, fetchCollections, fetchCollectionSection } from "src/redux/actions/collections";
import InfiniteScroll from "src/components/infiniteScroll";
import { USER_DEFINED } from "src/constants/collections";
import { useSpring, animated } from "@react-spring/web";
import { Stream } from "src/types/streams";
import { Collection, CollectionSection } from "src/types/collections";
import Thumbnail from "src/components/thumbnail";
import { FileService } from "src/utils/file.service";
import useMeasure from "react-use-measure";
import { fetchGroupChildren } from "src/redux/actions/groups";
import { SelectEntityContext } from "../index";
import { EdithubContext } from "../../index";
import Text from "src/components/text";

interface EntityBrowserProps {
  className?: string;
  width: number;
  entity?: Stream | Collection | CollectionSection;
  items?: any[];
  // eslint-disable-next-line no-unused-vars
  action?: (entity: any) => React.ReactNode;
  // eslint-disable-next-line no-unused-vars
  onSelection: (entity: any) => void;
  loading?: boolean;
}

const EntityBrowser: React.FC<EntityBrowserProps> = ({loading, className, width, items, action, onSelection }) => {
  return (
    <div className={cx(styles.container, className)}>

      {_.size(items) === 0 && !loading && <p className={styles.errorMessage}>No results found</p>}

      {items && items.map((item) => (
        <EntityRow key={item.id} entity={item} width={width} action={action} onSelection={onSelection} />
      ))}
    </div>
  );
}


const StreamsBrowser: React.FC<EntityBrowserProps> = ({ className, width, action, onSelection }) => {
  const dispatch = useDispatch();
  const { loading, streams } = useSelector((state: any) => {
    return {
      loading: state.hub.streams.loading,
      streams: state.hub.streams?.items,
    };
  }, _.isEqual);

  useEffect(() => {
    dispatch(fetchStreams());
  }, []);

  return (
    <div className={cx(styles.container, styles.stacks)}>
      <Reveal show={loading} className={styles.stack}>
        <StreamPlaceholder width={'50%'} />
        <StreamPlaceholder width={'70%'} />
        <StreamPlaceholder width={'60%'} />
        <StreamPlaceholder width={'50%'} />
      </Reveal>
      <Reveal show={!loading} className={styles.stack}>
        <EntityBrowser loading={loading} className={className} items={streams} width={width} action={action} onSelection={onSelection} />
      </Reveal>
    </div>
  );
}

const StreamBrowser: React.FC<EntityBrowserProps> = ({ className, width, entity, action, onSelection }) => {
  const dispatch = useDispatch();
  const { loading, items } = useSelector((state: any) => {
    if (!entity) {
      return {
        loading: false,
        items: []
      }
    }

    return {
      loading: state.stream.items[entity!.id!]?.loadingChildren,
      items: state.stream.items[entity!.id!]?.item?.children,
    };
  }, _.isEqual);

  useEffect(() => {
    if (entity?.id) {
      dispatch(fetchStreamChildren({
        id: entity.id,
        page: 0,
        limit: 200,
        sort: {
          type: 'name',
          order: 'asc'
        }
      }));
    }
  }, [entity?.id]);

  return (
    <div className={cx(styles.container, styles.stacks)}>
      <InfiniteScroll className={styles.scroll}
        loading={loading}
        hasMore={false}
        loadMore={() => { }}
        loader={() => {
          return (
            <>
              {_.times(_.size(items) === 0 ? 3 : 1, (index) => (
                <AssetPlaceholder key={index} />
              ))}
            </>
          )
        }}
      >
        <EntityBrowser loading={loading} className={className} width={width} items={items} action={action} onSelection={onSelection} />
      </InfiniteScroll>
    </div>
  );
}

const CollectionsBrowser: React.FC<EntityBrowserProps> = ({ className, width, action, onSelection }) => {
  const dispatch = useDispatch();

  const { loading, collections, nextPage, hasMoreItems } = useSelector((state: any) => {
    const collectionState = _.get(state, 'collections');
    const loading = _.get(collectionState, 'loading');
    const pagination = _.get(collectionState, 'pagination');
    const total = _.get(pagination, "totalPages", 0);
    const hasMoreItems = _.get(pagination, "hasMoreItems");

    const collections = (_.get(collectionState, 'items') || []).filter((item: any) => {
      return item.type === USER_DEFINED;
    });

    let nextPage: number | null = _.get(pagination, "currentPage", 0) + 1;
    if (nextPage && nextPage >= total) {
      nextPage = null;
    }

    return {
      loading: loading,
      collections: collections,
      nextPage: nextPage,
      hasMoreItems: hasMoreItems
    };
  }, _.isEqual);

  useEffect(() => {
    fetchCollectionsForPage(0);
  }, []);

  const fetchCollectionsForPage = (page: number) => {

    const request = {
      page: page,
      limit: 20,
      sort: {
        field: 'name',
        order: 'asc'
      }
    };

    dispatch(fetchCollections(request));
  }

  const handleLoadMoreCollections = () => {
    if (nextPage) {
      fetchCollectionsForPage(nextPage);
    }
  }

  return (
    <div className={cx(styles.container, styles.stacks)}>
      <InfiniteScroll className={styles.scroll}
        loading={loading}
        hasMore={hasMoreItems}
        loadMore={handleLoadMoreCollections}
        loader={() => {
          if (_.size(collections) === 0) {
            return (<>
              <CollectionPlaceholder width={'50%'} />
              <CollectionPlaceholder width={'70%'} />
              <CollectionPlaceholder width={'60%'} />
              <CollectionPlaceholder width={'50%'} />
            </>)
          } else {
            return <CollectionPlaceholder width={'70%'} />
          }
        }}
      >
        <EntityBrowser className={className}
          width={width}
          items={collections}
          action={action}
          onSelection={onSelection}
          loading={loading}
        />
      </InfiniteScroll>
    </div>
  );
}

const CollectionBrowser: React.FC<EntityBrowserProps> = ({ className, width, entity, action, onSelection }) => {
  const dispatch = useDispatch();
  const collection = entity as Collection;
  const childrenType = collection.sectionsCount! > 0 ? 'collection_section' : 'asset';

  const { loading, items } = useSelector((state: any) => {
    if (!entity) {
      return {
        loading: false,
        items: []
      }
    }

    const collection = state.collections.collection[entity!.id!]?.item;
    let items = [];
    if (collection) {
      if (_.size(collection.sections) > 0) {
        items = (collection.sections || []).map((item: any) => {
          return {
            ...item.item,
            entityType: 'collection_section'
          }
        });
      } else if (_.size(collection.assets) > 0) {
        items = (collection.assets || []).map((item: any) => {
          return {
            ...item.asset
          }
        });
      }
    }

    return {
      loading: state.collections.collection[entity!.id!]?.loading,
      items: items,
    };
  }, _.isEqual);

  useEffect(() => {
    if (entity?.id) {
      dispatch(fetchCollection(entity.id));
    }
  }, [entity?.id]);

  return (
    <div className={cx(styles.container, styles.stacks)}>
      <Reveal show={loading} className={styles.stack}>
        {childrenType === 'collection_section' && (
          <>
            <CollectionSectionPlaceholder width="50%" />
            <CollectionSectionPlaceholder width="70%" />
            <CollectionSectionPlaceholder width="60%" />
            <CollectionSectionPlaceholder width="50%" />
          </>
        )}

        {childrenType === 'asset' && (
          <>
            {_.times((collection.assetsCount || 3), (index) => (
              <AssetPlaceholder key={index} />
            ))}
          </>
        )}

      </Reveal>
      <Reveal show={!loading} className={styles.stack}>
        <EntityBrowser loading={loading} className={className} items={items} width={width} action={action} onSelection={onSelection} />
      </Reveal>
    </div>
  );
}


/**
 * Entity Renders
*/

interface EntityRowProps {
  entity: any;
  width: number;
  // eslint-disable-next-line no-unused-vars
  action?: (entity: any) => React.ReactNode;
  // eslint-disable-next-line no-unused-vars
  onSelection: (entity: any) => void;
}

const EntityRow: React.FC<EntityRowProps> = ({ entity, width, action, onSelection }) => {
  const Component = useMemo(() => {
    const type = entity?.entityType;
    switch (type) {
      case 'stream':
        return StreamRow;
      case 'collection':
        return CollectionRow;
      case 'asset':
        return AssetRow;
      case 'group':
        return GroupRow;  
      default:
        return false;
    }
  }, [entity]);


  if (!Component) {
    return null;
  }

  return (
    React.createElement(Component, { entity, width, action, onSelection })
  );
}

const GroupRow: React.FC<EntityRowProps> = ({ entity, width, action, onSelection }) => {
  const [expanded, setExpanded] = React.useState(false);
    const [mountChildren, setMountChildren] = React.useState(false);
    const [containerRef, containerBounds] = useMeasure();
    const [childrenAnimation] = useSpring({
      opacity: expanded ? 1 : 0,
      height: expanded ? containerBounds.height : 0,
      from: { opacity: 0, height: 0 },
    }, [expanded, containerBounds]);
  
    const handleClick = () => {
      setExpanded(!expanded);
      setMountChildren(true);
    }
  
    return (
      <>
        <div className={cx(styles.entity, styles.expandable, { [styles.expanded]: expanded })} style={{ width: width }} onClick={handleClick}>
          <Text className={styles.name} text={entity?.name} />
          <div className={styles.actions}>
            <button className={styles.action}>
              <i className={cx(styles.arrow, "paperflite-dropdown")}></i>
            </button>
          </div>
        </div>
        <animated.div style={childrenAnimation}>
          {mountChildren && (
            <animated.div style={childrenAnimation} className={styles.children}>
              <div ref={containerRef} className={styles.inner}>
                <GroupBrowser entity={entity} width={width - 15} action={action} onSelection={onSelection} />
              </div>
            </animated.div>
          )}
        </animated.div>
      </>
    );
}
const GroupBrowser: React.FC<EntityBrowserProps> = ({ className, width, entity, action, onSelection }) => {
  const dispatch = useDispatch();
  const { loading, items } = useSelector((state: any) => {
    if (!entity) {
      return {
        loading: false,
        items: []
      }
    }

    return {
      loading: state.group[entity!.id!]?.loadingChildren,
      items: state.group[entity!.id!]?.item?.children,
    };
  }, _.isEqual);

  useEffect(() => {
    if (entity?.id) {
      dispatch(fetchGroupChildren({
        id: entity.id,
        page: 0,
        limit: 200,
        sort: {
          type: 'name',
          order: 'asc'
        }
      }));
    }
  }, [entity?.id]);

  return (
    <div className={cx(styles.container, styles.stacks)}>
      <InfiniteScroll className={styles.scroll}
        loading={loading}
        hasMore={false}
        loadMore={() => { }}
        loader={() => {
          return (
            <>
              {_.times(_.size(items) === 0 ? 3 : 1, (index) => (
                <AssetPlaceholder key={index} />
              ))}
            </>
          )
        }}
      >
        <EntityBrowser className={className} width={width} items={items} action={action} onSelection={onSelection} loading={loading} />
      </InfiniteScroll>
    </div>
  );
}
const AssetRow: React.FC<EntityRowProps> = ({ entity, width, action, onSelection }) => {

  const { selectedEntity } = React.useContext(SelectEntityContext);
  const [showTooltip, setShowTooltip] = React.useState(false);

  const fileType = useMemo(() => {
    return FileService.getFileType(entity.metadata);
  }, [entity]);

  const updatedDate = _.get(entity, 'contentModifiedDate');
  const internal = !_.get(entity, 'settings.share', false);

  const handleClick = () => {
    if (onSelection) {
      onSelection(entity);
    }
  }

  const isEntitySelected = React.useMemo(
    () => !!_.find(selectedEntity, { id: entity.id }),
    [selectedEntity, entity.id],
  );

  return (
    <>
      <div className={cx(styles.entity, styles.asset)}  onClick={handleClick}>
        <div className={styles.inner}>
          <Thumbnail className={styles.icon} media={entity?.icon} width={68} height={80} defaultImage="/images/assets/default_asset.jpg" />
          <div className={styles.details}>
            <div className={styles.innerContainer}>
              <Text className={styles.name} text={entity?.name} />
              <div className={styles.meta}>
                {fileType} {updatedDate && `| Updated ${dateformat(updatedDate, 'dd mmm yyyy')}`}
              </div>
            </div>
            <div className={styles.date}>
              {updatedDate && `Updated ${dateformat(updatedDate, 'dd mmm yyyy')}`}
            </div>
            <div className={styles.indicators}>
            {internal &&
              <Tooltip content={'Internal use only'} placement='right'>
                <i className="paperflite-lock"></i>
              </Tooltip>
            }
            </div>
          </div>
        </div>
        {!internal &&
        <div className={styles.actions}>
          <button
            onClick={(e) => {
              e.stopPropagation(); // Prevent triggering handleClick
              onSelection(entity); // Select or deselect the entity
            }}
            className={cx(styles.action, styles.selectIcon, {
              [styles.activeSelect]: isEntitySelected,
            })}
          >
            <i
              className={cx(
                styles.arrow,
                isEntitySelected
                  ? "fa-solid fa-check-circle"
                  : "paperflite-radio-button-unselected",
              )}
            />
          </button>
        </div>
        }
      </div>

    </>
  );
}

const StreamRow: React.FC<EntityRowProps> = ({
  entity,
  width,
  action,
  onSelection,
}) => {
  const [expanded, setExpanded] = React.useState(false);
  const [mountChildren, setMountChildren] = React.useState(false);
  const [containerRef, containerBounds] = useMeasure();
  const { contentType,  selectedEntity} = React.useContext(SelectEntityContext);
  const { showEntityTree, activeColumnIndex } = React.useContext(EdithubContext)
  const [showTooltip, setShowTooltip] = React.useState(false);
 
  useEffect(() => {
    setExpanded(false);
  },[contentType, activeColumnIndex, showEntityTree])

  // Check if the entity is selected by searching in the selectedEntity array
  const isEntitySelected = React.useMemo(
    () => !!_.find(selectedEntity, { id: entity.id }),
    [selectedEntity, entity.id],
  );

  const [childrenAnimation] = useSpring(
    {
      opacity: expanded ? 1 : 0,
      height: expanded ? containerBounds.height : 0,
      from: { opacity: 0, height: 0 },
    },
    [expanded, containerBounds],
  );

  const handleClick = () => {
    if (contentType !== "asset") {
      onSelection(entity); // Select or deselect the entity
    } else {
      setExpanded(!expanded);
      setMountChildren(true);
    }
  };

  return (
    <>
      <div
        className={cx(styles.entity, styles.expandable, styles.collection)}
        onClick={handleClick}
      >
        <div className={styles.inner}>
          <Text text={entity?.name} className={styles.name} />
          {contentType === "asset" && <button className={styles.action}>
            <i className={cx(styles.arrow, "paperflite-dropdown")}></i>
          </button>}

        </div>

        <div className={styles.actions}>
          {contentType !== "asset" && <button
            onClick={(e) => {
              e.stopPropagation(); // Prevent triggering handleClick
              onSelection(entity); // Select or deselect the entity
            }}
            className={cx(styles.action, styles.selectIcon, {
              [styles.activeSelect]: isEntitySelected,
            })}
          >
            <i
              className={cx(
                styles.arrow,
                isEntitySelected
                  ? "fa-solid fa-check-circle"
                  : "paperflite-radio-button-unselected",
              )}
            />
          </button>}
        </div>
      </div>

      {mountChildren && (
        <animated.div style={childrenAnimation} className={styles.children}>
          <div ref={containerRef} className={styles.inner}>
            <StreamBrowser
              entity={entity}
              width={width - 15}
              action={action}
              onSelection={onSelection}
            />
          </div>
        </animated.div>
      )}
    </>
  );
};

const CollectionRow: React.FC<EntityRowProps> = ({ entity, width, action, onSelection }) => {
  const [showTooltip, setShowTooltip] = React.useState(false);
  const [expanded, setExpanded] = React.useState(false);
  const [mountChildren, setMountChildren] = React.useState(false);
  const [containerRef, containerBounds] = useMeasure();
  const { selectedEntity } =
    React.useContext(SelectEntityContext);
  let isEntitySelected = _.find(selectedEntity, { id: entity.id });

  const [childrenAnimation] = useSpring({
    opacity: expanded ? 1 : 0,
    height: expanded ? containerBounds.height : 0,
    from: { opacity: 0, height: 0 },
  }, [expanded, containerBounds]);

  const handleClick = () => {
    onSelection(entity);
  }


  return (
    <>
      <div className={cx(styles.entity, styles.expandable, styles.collection)} onClick={handleClick}>
        <div className={styles.inner}>
          <div className={styles.name} title={entity?.name}>
            <Text text={entity?.name} tooltip={{placement: "auto"}} />
          </div>
        </div>
        <div className={styles.actions}>
          <button className={cx(styles.action, styles.selectIcon, {
            [styles.activeSelect]: isEntitySelected
          })}>
            <i
              className={cx(styles.arrow, isEntitySelected ? "fa-solid fa-check-circle" : 'paperflite-radio-button-unselected'
              )} />
          </button>
        </div>
      </div>
      {mountChildren && (
        <animated.div style={childrenAnimation} className={styles.children}>
          <div ref={containerRef} className={styles.inner}>
            <CollectionBrowser entity={entity} width={width - 15} action={action} onSelection={onSelection} />
          </div>
        </animated.div>
      )}
    </>
  );
}

export const StreamPlaceholder: React.FC<{ width: string }> = ({ width }) => {
  return (
    <div className={cx(styles.entity, styles.placeholder)} style={{ width: width }}>
      <div className={cx(styles.name, styles.placeholderName, styles.block)}></div>
    </div>
  );
}

export const CollectionPlaceholder: React.FC<{ width: string }> = ({ width }) => {
  return (
    <div className={cx(styles.entity, styles.placeholder)} style={{ width: width }}>
      <div className={cx(styles.name, styles.placeholderName, styles.block)}></div>
    </div>
  );
}

export const CollectionSectionPlaceholder: React.FC<{ width: string }> = ({ width }) => {
  return (
    <div className={cx(styles.entity, styles.placeholder)} style={{ width: width }}>
      <div className={cx(styles.name, styles.placeholderName, styles.block)}></div>
    </div>
  );
}

export const AssetPlaceholder: React.FC<any> = () => {
  return (
    <div className={cx(styles.entity, styles.asset, styles.placeholder)}>
      <div className={styles.inner}>
        <div className={cx(styles.icon, styles.block)}></div>
        <div className={styles.details}>
          <div className={cx(styles.name, styles.block)}></div>
          <div className={styles.meta}>
            <span className={styles.block} />
          </div>
        </div>
      </div>
    </div>
  );
}

export { EntityBrowser, StreamsBrowser, CollectionsBrowser };